import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { ResearchTopic, ViewMode } from "../types"
import { useEffect, useState } from 'react'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const d = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
  
  return formatDate(d)
}

export function formatEngagementCount(count: number): string {
  if (count < 1000) return count.toString()
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`
  return `${(count / 1000000).toFixed(1)}M`
}

export function normalizeResearchTitle(title: string): string {
  if (!title) return ''
  let sanitized = title.trim()
  // Remove wrapping quotes first
  sanitized = sanitized.replace(/^["'“”‘’`]+/, '').replace(/["'“”‘’`]+$/, '').trim()
  // Remove wrapping parentheses (multiple layers)
  while (sanitized.startsWith('(') && sanitized.endsWith(')')) {
    sanitized = sanitized.slice(1, -1).trim()
  }
  // Remove any remaining parentheses characters
  sanitized = sanitized.replace(/[()]/g, ' ').trim()
  // Collapse repeated whitespace
  sanitized = sanitized.replace(/\s{2,}/g, ' ').trim()
  return sanitized
}

export function getViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'desktop'
  
  const width = window.innerWidth
  if (width < 768) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substr(0, maxLength).trim() + '...'
}

export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    technology: 'bg-blue-500',
    business: 'bg-green-500',
    politics: 'bg-red-500',
    science: 'bg-purple-500',
    health: 'bg-pink-500',
    culture: 'bg-yellow-500',
    environment: 'bg-emerald-500',
    finance: 'bg-indigo-500',
  }
  return colors[category.toLowerCase()] || 'bg-gray-500'
}

export function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    technology: '💻',
    business: '💼',
    politics: '🏛️',
    science: '🔬',
    health: '🏥',
    culture: '🎭',
    environment: '🌍',
    finance: '💰',
  }
  return icons[category.toLowerCase()] || '📄'
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

export function parseResearchFiles(files: FileList | File[]): ResearchTopic[] {
  // This would parse markdown files from the backend output directory
  // For now, return mock data
  return []
}

export function extractImageFromMarkdown(markdown: string): string | undefined {
  const imageRegex = /!\[.*?\]\((.*?)\)/
  const match = markdown.match(imageRegex)
  return match ? match[1] : undefined
}

export function generateGradient(seed: string): string {
  // Generate a consistent gradient based on a seed string
  const colors = [
    'from-blue-400 to-purple-600',
    'from-green-400 to-blue-600',
    'from-purple-400 to-pink-600',
    'from-yellow-400 to-red-600',
    'from-indigo-400 to-purple-600',
    'from-pink-400 to-red-600',
    'from-teal-400 to-blue-600',
    'from-orange-400 to-pink-600',
  ]
  
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  
  return colors[Math.abs(hash) % colors.length]
}

export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

export function getSwipeThreshold(): number {
  return isTouchDevice() ? 100 : 150
}

export function hapticFeedback(type: 'light' | 'medium' | 'heavy' = 'light') {
  if ('vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30]
    }
    navigator.vibrate(patterns[type])
  }
} 

// Custom hook for scroll-based navigation hiding
export function useScrollNavigation() {
  const [isNavVisible, setIsNavVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const handleScroll = () => {
      // Debounce scroll events to prevent flickering
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        // Check if we're in an ArticlePage with a scroll container
        const articleContainer = document.getElementById('article-scroll-container')
        
        let currentScrollY: number
        
        if (articleContainer) {
          // Use the scroll position of the article container
          currentScrollY = articleContainer.scrollTop
        } else {
          // Use window scroll position for normal pages
          currentScrollY = window.scrollY
        }
        
        // Show navigation when:
        // 1. Scrolling up by at least 5px (prevent tiny movements)
        // 2. At the top of the page
        if (currentScrollY < lastScrollY - 5 || currentScrollY < 100) {
          setIsNavVisible(true)
        }
        // Hide navigation when scrolling down past a threshold by at least 10px
        else if (currentScrollY > lastScrollY + 10 && currentScrollY > 200) {
          setIsNavVisible(false)
        }
        
        setLastScrollY(currentScrollY)
      }, 50) // Debounce to smooth out scroll events and prevent flickering
    }

    // Listen to both window and article container scroll events
    window.addEventListener('scroll', handleScroll, { passive: true })
    
    const articleContainer = document.getElementById('article-scroll-container')
    if (articleContainer) {
      articleContainer.addEventListener('scroll', handleScroll, { passive: true })
    }

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', handleScroll)
      const articleContainer = document.getElementById('article-scroll-container')
      if (articleContainer) {
        articleContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [lastScrollY])
  
  // Ensure navigation is visible when article container appears
  useEffect(() => {
    const articleContainer = document.getElementById('article-scroll-container')
    if (articleContainer) {
      setIsNavVisible(true)
      setLastScrollY(0) // Reset scroll tracking
    }
  }, [])

  return isNavVisible
} 

export function generateAvatarFallback(email: string | undefined, name: string | undefined): string {
  const seed = email || name || 'default'
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}`
}

// Utility function to get proper research title
export function getResearchTitle(research: any): string {
  const query = research.query || ''
  
  // Check if a title is a placeholder or just the query text
  const isPlaceholderOrQuery = (title: string) => {
    if (!title) return true
    const normalized = title.trim().toLowerCase()
    const queryNormalized = query.trim().toLowerCase()
    // Placeholder values
    if (normalized === 'research in progress...' || normalized === 'untitled research' || normalized === 'intelligence report') {
      return true
    }
    // Title equals the query (not a real title)
    if (queryNormalized && normalized === queryNormalized) {
      return true
    }
    // Title is just a truncated version of the query
    if (queryNormalized && queryNormalized.startsWith(normalized.replace('...', ''))) {
      return true
    }
    return false
  }
  
  // If title exists and is a real title (not placeholder or query), use it
  if (research.title && !isPlaceholderOrQuery(research.title)) {
    const cleaned = normalizeResearchTitle(research.title)
    if (cleaned) {
      return cleaned
    }
  }
  
  // If we have results with a title, use that
  if (research.results?.title && !isPlaceholderOrQuery(research.results.title)) {
    const cleaned = normalizeResearchTitle(research.results.title)
    if (cleaned) {
      return cleaned
    }
  }
  
  // Extract title from report content if available
  if (research.results?.report_text || research.results?.content) {
    const content = research.results.report_text || research.results.content
    const titleMatch = content.match(/^#\s+(.+)$/m)
    if (titleMatch && titleMatch[1]) {
      const extracted = titleMatch[1].trim()
      // Skip common section headers
      if (!['Executive Summary', 'Background & Context', 'Introduction', 'Researcher Profile', 'Table of Contents']
          .includes(extracted)) {
        return extracted
      }
    }
  }
  
  // Fallback to query if available - capitalize first letter for better display
  if (query) {
    const cleanedQuery = normalizeResearchTitle(
      query.length > 80 ? query.substring(0, 77) + '...' : query
    )
    if (cleanedQuery) {
      // Capitalize first letter
      return cleanedQuery.charAt(0).toUpperCase() + cleanedQuery.slice(1)
    }
  }
  
  // Last resort
  return 'Untitled Research'
} 